import React from 'react';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectTicketActionEventNewValue,
    selectTicketActionEventPrevValue,
} from 'flow/selectors/TicketEventSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketActionEventKey } from 'flow/types/TicketEventData';
import ActionEventRow from './ActionEventRow';
import useActorTooltipLine from './useActorTooltipLine';

interface Props {
    ticketKey: TicketKey;
    eventKey: TicketActionEventKey;
}

const ActionEventSubjectChanged = ({ ticketKey, eventKey }: Props) => {
    const { i18next } = useAppContext();

    const newValue = useAppSelector((state) =>
        selectTicketActionEventNewValue(state, ticketKey, eventKey),
    );
    const prevValue = useAppSelector((state) =>
        selectTicketActionEventPrevValue(state, ticketKey, eventKey),
    );
    const actorLine = useActorTooltipLine(ticketKey, eventKey);

    const tooltipParts: string[] = [];
    if (prevValue) {
        tooltipParts.push(
            i18next.t('tickets.tooltip.previously', { value: prevValue }),
        );
    }
    if (actorLine) {
        tooltipParts.push(actorLine);
    }

    return (
        <ActionEventRow
            ticketKey={ticketKey}
            eventKey={eventKey}
            tooltipLabel={tooltipParts.join(' · ')}
        >
            {newValue ? (
                <Trans
                    i18n={i18next}
                    i18nKey="tickets.subject.set"
                    values={{ subject: newValue }}
                    components={[<strong key="subject" />]}
                />
            ) : (
                i18next.t('tickets.subject.removed')
            )}
        </ActionEventRow>
    );
};

export default React.memo(ActionEventSubjectChanged);
